/**
 * Classifies a file into a display kind from its MIME type, falling back to its
 * filename extension. Pure — no React, no colour. The icon component owns
 * presentation; this owns the decision.
 *
 * Kept separate from ALLOWED_FILE_TYPES in schemas.ts on purpose: that map
 * gates *uploads* and is keyed by MIME. This one has to classify anything we
 * might *display*, including files that arrived via SharePoint with no MIME
 * type at all.
 */

export type FileKind =
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'csv'
  | 'text'
  | 'image'
  | 'generic'

export const FILE_KIND_LABEL: Record<FileKind, string> = {
  pdf: 'PDF',
  word: 'Word document',
  excel: 'Excel spreadsheet',
  powerpoint: 'PowerPoint presentation',
  csv: 'CSV file',
  text: 'Text file',
  image: 'Image',
  generic: 'File',
}

/**
 * Keys MUST be lowercase — lookups lowercase the input first. Browsers report
 * File.type in lowercase, so the canonical `...macroEnabled.12` spelling would
 * otherwise never match.
 */
const MIME_TO_KIND: Record<string, FileKind> = {
  'application/pdf': 'pdf',
  'application/msword': 'word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  'application/vnd.ms-excel.sheet.macroenabled.12': 'excel',
  'application/vnd.ms-powerpoint': 'powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
  'text/csv': 'csv',
}

const EXTENSION_TO_KIND: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  xls: 'excel',
  xlsx: 'excel',
  xlsm: 'excel',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  csv: 'csv',
  txt: 'text',
  md: 'text',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
}

function kindFromMimeType(mimeType: string): FileKind {
  const normalized = mimeType.trim().toLowerCase()
  const exact = MIME_TO_KIND[normalized]
  if (exact) return exact
  if (normalized.startsWith('text/')) return 'text'
  if (normalized.startsWith('image/')) return 'image'
  return 'generic'
}

function kindFromFileName(fileName: string): FileKind {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot < 0 || lastDot === fileName.length - 1) return 'generic'
  const extension = fileName.slice(lastDot + 1).trim().toLowerCase()
  return EXTENSION_TO_KIND[extension] ?? 'generic'
}

/**
 * MIME type wins when it is recognised. The filename is only consulted when the
 * MIME type is absent or unhelpful — SharePoint items and some uploads arrive
 * as null or `application/octet-stream`.
 */
export function getFileKind(
  mimeType?: string | null,
  fileName?: string | null
): FileKind {
  if (mimeType) {
    const kind = kindFromMimeType(mimeType)
    if (kind !== 'generic') return kind
  }
  if (fileName) return kindFromFileName(fileName)
  return 'generic'
}
